import { Suspense, lazy, useEffect, useState, useRef, memo, type ComponentType } from "react";
import "./styles/App.css";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import About from "./components/About";
import Experience from "./components/Experience";
import Achievements from "./components/Achievements";
import Footer from "./components/Footer";
import ErrorBoundary from "./components/ErrorBoundary";
import { DevModeProvider, useDevMode } from "./components/DeveloperMode";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import DebugPanel from "./components/DebugPanel";
import LazyOnViewport from "./components/LazyOnViewport";
import { getActiveSections, getFooterEnabled } from "./config/sections";
import { DebugProvider, useDebug } from "./contexts/DebugContext";
import { RouterProvider, useRouter, getSavedScrollPosition } from "./lib/router";

import CustomCursor from "./components/CustomCursor";
import PrivacyPage from "./components/PrivacyPage";
import TermsPage from "./components/TermsPage";
import NotFoundPage from "./components/NotFoundPage";
import { isMobileDevice } from "./utils/appLinks";

/* CanvasBackground uses plain React.lazy (not lazyWithPriority) because it renders via Suspense
   directly in the template (line 363-365), not through the section map. It is excluded from
   the priority system. */
const CanvasBackground = lazy(() => import("./components/CanvasBackground"));
const ThemeTransition = lazy(() => import("./components/ThemeTransition"));
const SamsungNotice = lazy(() => import("./components/SamsungNotice"));

/* Module-scoped dynamic import map so the global hashchange listener (line 53) and lazyWithPriority
   callbacks can access them across component re-renders. If inside a component, they'd be recreated
   on every render, breaking eager-load references. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const importModules: Record<string, () => Promise<{ default: ComponentType<any> }>> = {
  projects: () => import("./components/Projects"),
  "side-projects": () => import("./components/SideProjects"),
  archive: () => import("./components/Archive"),
  education: () => import("./components/Education"),
  recommendations: () => import("./components/Recommendations"),
  resume: () => import("./components/Resume"),
  faq: () => import("./components/FAQ"),
}

/* DelayedMount defers rendering its children by `delay` ms (default 500ms).
   500ms default delay allows above-the-fold content (hero, navbar) to render and settle before
   non-critical UI (like cursor) appears. Prevents layout thrashing on initial load. */
const DelayedMount = ({ delay = 500, children }: { delay?: number; children: React.ReactNode }) => {
  const [show, setShow] = useState(false)
  useEffect(() => {
    const timer = setTimeout(() => setShow(true), delay)
    return () => clearTimeout(timer)
  }, [delay])
  return show ? <>{children}</> : null
}

/* These MUST be module-scoped (not inside a component). The global hashchange listener and
   updatePriorityMap function access them across renders. If they were inside App(), they'd reset
   on every re-render, breaking hash-based preloading across navigation. */

/* Registry of immediate-resolve callbacks keyed by section ID. The hashchange listener invokes these to eagerly load the targeted section. */
const importCallbacks: Record<string, () => void> = {};

/* Priority weights (lower = sooner) assigned to each lazy section via debug config. Controls how long lazyWithPriority delays before resolving. */
const priorityMap: Record<string, number> = {}
/* Updates priorityMap at runtime so debug panel changes take effect without a reload. */
// eslint-disable-next-line react-refresh/only-export-components
export function updatePriorityMap(map: Record<string, number>) {
  Object.assign(priorityMap, map)
}

/* This runs BEFORE React mounts. It eagerly triggers import of hash-targeted sections.
   Even if the React tree hasn't rendered yet, the chunk starts downloading.
   Intentional for instant navigation on refresh with a URL hash. */
if (typeof window !== "undefined") {
  window.addEventListener("hashchange", () => {
    const hash = window.location.hash;
    if (hash) {
      const section = hash.substring(1);
      if (importCallbacks[section]) {
        importCallbacks[section]();
      }
    }
  });
}

/* Priority-aware lazy loading HOC. Resolves the import immediately if this section is the hash target.
   Otherwise waits a delay proportional to the section's priority weight. If a different section is hash-targeted,
   this section waits at least 1500ms to keep bandwidth focused on the target section.
   The resolved guard flag prevents double-resolution from both the hash-match path and the timer path.
   Both could fire on initial load with a hash. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const lazyWithPriority = (importFunc: () => Promise<{ default: ComponentType<any> }>, sectionId: string) => {
  return lazy(() => {
    return new Promise<{ default: ComponentType<any> }>((resolve) => { // eslint-disable-line @typescript-eslint/no-explicit-any
      /* The resolved guard flag prevents double-resolution from both the hash-match path (lines 82-86)
         and the timer path (lines 98-101). Both could fire on initial load with a hash.
         Ensures the import promise is settled at most once. */
      let resolved = false;
      const doResolve = () => {
        if (resolved) return;
        resolved = true;
        resolve(importFunc());
      };

      /* Register the resolve callback so the hashchange listener can eagerly resolve this section. */
      importCallbacks[sectionId] = doResolve;

      /* Evaluated at definition time (during lazy() call, at module evaluation), not reactive.
         If this section is already the current hash target, resolve immediately without delay.
         This is a one-time check when the module loads, before React even mounts. */
      const hash = typeof window !== "undefined" ? window.location.hash : "";
      if (hash === `#${sectionId}`) {
        doResolve();
        return;
      }

      /* hasOtherTarget: if the user navigated directly to a different section (e.g. #projects),
         this section gets a longer delay (1500ms minimum) to deprioritize non-target sections. */
      const lazySections = ["achievements", "projects", "side-projects", "archive", "education", "recommendations", "resume", "faq"];
      const currentTarget = hash ? hash.substring(1) : "";
      const hasOtherTarget = currentTarget && lazySections.includes(currentTarget) && currentTarget !== sectionId;

      /* 50ms per priority level allows fine-grained ordering. 200ms base ensures non-priority
         sections get a reasonable delay. If a different section is hash-targeted (e.g. #projects),
         this section gets a longer delay (1500ms min) to prioritize the target section. */
      const priority = priorityMap[sectionId.toUpperCase()] ?? 99;
      const baseDelay = priority * 50 + 200;
      const delay = hasOtherTarget ? Math.max(baseDelay, 1500) : baseDelay;

      /* Start the timer that will resolve the import after the calculated delay. */
      const timer = setTimeout(doResolve, delay);

      /* Dual-registration pattern: the first register (line 80) is immediately replaced by this one
         that includes clearTimeout. This allows hash-navigation to preempt the timer:
         when hashchange fires for this section, cancel the timer and resolve instantly.
         Without clearTimeout, both the timer and the hashchange would resolve the same import. */
      importCallbacks[sectionId] = () => {
        clearTimeout(timer);
        doResolve();
      };
    });
  });
};

/* Priority-aware lazy components for each dynamic section. Created at module scope so React.lazy identity is stable across renders. */
const LazyProjects = lazyWithPriority(() => import("./components/Projects"), "projects");
const LazySideProjects = lazyWithPriority(() => import("./components/SideProjects"), "side-projects");
const LazyArchive = lazyWithPriority(() => import("./components/Archive"), "archive");
const LazyEducation = lazyWithPriority(() => import("./components/Education"), "education");
const LazyRecommendations = lazyWithPriority(() => import("./components/Recommendations"), "recommendations");
const LazyResume = lazyWithPriority(() => import("./components/Resume"), "resume");
const LazyFAQ = lazyWithPriority(() => import("./components/FAQ"), "faq");

/* Static map for Suspense-based rendering path (Strategy 0 fallback). These are pre-configured lazy
   components created via lazyWithPriority at module scope, not dynamically loaded at render time.
   Used when no background preload (Strategy 2) or IntersectionObserver (Strategy 1) is active. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const lazyComponentMap: Record<string, ComponentType<any>> = {
  projects: LazyProjects,
  "side-projects": LazySideProjects,
  archive: LazyArchive,
  education: LazyEducation,
  recommendations: LazyRecommendations,
  resume: LazyResume,
  faq: LazyFAQ,
};

/* Shimmer/skeleton placeholder shown while a lazy section is loading. Displays animated gradient lines inside a glass-panel card.
   aria-hidden="true" hides shimmer content from screen readers intentionally (skeleton is decorative, not informative).
   However, the <h2> heading structure is temporarily broken during loading -- known tradeoff. */
const SectionPlaceholder = ({ minHeight, title }: { minHeight: string; title: string }) => (
  <section
    className="section-placeholder"
    ref={(el) => {
      if (el) {
        el.style.setProperty("--placeholder-min-height", minHeight);
      }
    }}
    aria-hidden="true"
  >
    <div className="container">
      <h2 className="placeholder-title">{title}</h2>
      <div className="placeholder-card glass-panel">
        <div className="placeholder-shimmer-content">
          <div className="shimmer-line header-shimmer" />
          <div className="shimmer-line body-shimmer-1" />
          <div className="shimmer-line body-shimmer-2" />
        </div>
      </div>
    </div>
  </section>
);

const MemoizedAbout = memo(About);
const MemoizedExperience = memo(Experience);
const MemoizedAchievements = memo(Achievements);

/* Root app component. Sets up global effects: drag prevention, scroll restoration, and hash-based scroll navigation. */
function App() {
  /* .floating-badge check is a workaround for Hero.tsx animated badges (25M Downloads, 4.5+ Years) that
     would otherwise be draggable images. The redundant target.tagName === "A" and target.closest("a")
     are for different edge cases (direct anchor click vs nested anchor elements). */
  useEffect(() => {
    const handleDragStart = (e: DragEvent) => {
      const target = e.target as HTMLElement;
      if (
        target &&
        (target.tagName === "IMG" ||
          target.tagName === "A" ||
          (target.closest && (target.closest("a") || target.closest(".floating-badge"))))
      ) {
        e.preventDefault();
      }
    };
    document.addEventListener("dragstart", handleDragStart);
    return () => {
      document.removeEventListener("dragstart", handleDragStart);
    };
  }, []);

  /* Track the current URL hash so the hash-scroll effect can react to navigation changes. */
  const [targetHash, setTargetHash] = useState(window.location.hash);

  /* CRITICAL: history.scrollRestoration = "manual" is WHY the hash-scroll polling system (lines 188-259)
     exists. Without disabling automatic scroll restoration, the browser would fight the manual scroll-to
     logic. These two effects are COUPLED -- changing one breaks the other. */
  useEffect(() => {
    if ("scrollRestoration" in history) {
      history.scrollRestoration = "manual";
    }

    window.scrollTo(0, 0);

    const handleHashChange = () => {
      setTargetHash(window.location.hash);
    };

    window.addEventListener("hashchange", handleHashChange);
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  /* When targetHash changes, poll for the target element and scroll it into view. Cancels on user interaction
     (wheel, touch, scroll keys) to avoid fighting the user. Times out after 5s.
     userInteracted flag prevents forced scrolling from fighting user scroll. Three event listeners
     (wheel, touchmove, keydown) each cancel on interaction. */
  useEffect(() => {
    if (!targetHash) return;

    let userInteracted = false;
    let intervalId = 0;

    const cancelScroll = (e?: Event) => {
      if (e && e.type === "keydown") {
        const keyEvent = e as KeyboardEvent;
        /* Only navigation keys are filtered. Alphanumeric keys (typing) intentionally NOT filtered
           because they should not be mistaken for scroll intent. */
        const scrollKeys = ["ArrowUp", "ArrowDown", "Space", "PageUp", "PageDown", "Home", "End", " "];
        if (!scrollKeys.includes(keyEvent.key)) {
          return;
        }
      }
      userInteracted = true;
      cleanup();
    };

    /* Remove all interaction listeners and stop the polling interval. */
    const cleanup = () => {
      window.removeEventListener("wheel", cancelScroll);
      window.removeEventListener("touchmove", cancelScroll);
      window.removeEventListener("keydown", cancelScroll);
      if (intervalId) {
        clearInterval(intervalId);
      }
    };

    /* Detect user-initiated scrolling (wheel, touch, keyboard) to abort programmatic scroll. */
    window.addEventListener("wheel", cancelScroll, { passive: true });
    window.addEventListener("touchmove", cancelScroll, { passive: true });
    window.addEventListener("keydown", cancelScroll);

    /* Poll for the target element in the DOM. Once found, scroll into view with smooth behavior (unless user has interacted). */
    const checkAndScroll = () => {
      if (userInteracted) {
        cleanup();
        return true;
      }
      try {
        const element = document.querySelector(targetHash);
        if (element) {
          /* 50ms extra delay: element exists in DOM but may not have finished layout.
             Gives browser time to complete layout/reflow before scrolling. */
          setTimeout(() => {
            if (!userInteracted) {
              element.scrollIntoView({ behavior: "smooth" });
            }
          }, 50);
          cleanup();
          return true;
        }
      } catch {
        cleanup();
        return true;
      }
      return false;
    };

    /* 50ms polling is fast enough to catch lazy-loaded sections appearing as they render.
       5000ms hard timeout prevents infinite polling if section never appears (e.g. wrong hash, section disabled).
       Cleanup edge case: if targetHash changes while previous poll is running, the old cleanup might not have
       run yet. The useEffect return value handles this, but if both effects stagger, two intervals could briefly coexist. */
    if (!checkAndScroll()) {
      intervalId = window.setInterval(checkAndScroll, 50);
      const timeout = setTimeout(cleanup, 5000);

      return () => {
        cleanup();
        clearTimeout(timeout);
      };
    }

    return () => {
      cleanup();
    };
  }, [targetHash]);

  /* Render the provider hierarchy and delegate to AppContent for the actual UI. */
  return (
    <RouterProvider>
      <ThemeProvider>
        <DebugProvider>
          <DevModeProvider>
            <AppContent />
          </DevModeProvider>
        </DebugProvider>
      </ThemeProvider>
    </RouterProvider>
  );
}

/* Inner component that consumes all contexts (theme, dev mode, debug config) and renders the page sections. */
function AppContent() {
  const { theme } = useTheme();
  useDevMode();
  const { config: debug } = useDebug();
  const isMountedRef = useRef(false);

  /* CRITICAL: The "theme refresh hack." After React state changes the theme, CSS variables on :root update
     but the browser may not immediately repaint. Adding theme-refresh class, forcing synchronous reflow
     with getBoundingClientRect(), then removing on requestAnimationFrame forces the browser to flush
     pending CSS transitions. Without this, theme changes can show half-applied styles.
     We skip the getBoundingClientRect() call on initial mount (first run of effect) to avoid layout thrashing. */
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    const root = document.documentElement;
    root.classList.add("theme-refresh");
    root.getBoundingClientRect();
    requestAnimationFrame(() => {
      root.classList.remove("theme-refresh");
    });
  }, [theme]);

  /* SPA router from lib/router provides path and navigate. pushState-based navigation
     preserves React state across pages (no full reloads). */
  const { path: currentPage } = useRouter();

  /* Scroll-position cache: restores scrollY when navigating back to a previously visited path.
     Sub-pages (privacy, terms, 404) always scroll to top.
     Home with hash: let the hash-scroll polling effect handle it.
     Home without hash: restore saved scroll or go to top. */
  const prevPathRef = useRef<string | null>(null)

  useEffect(() => {
    if (prevPathRef.current === null) {
      prevPathRef.current = currentPage
      return
    }

    prevPathRef.current = currentPage

    if (currentPage !== '/') {
      window.scrollTo(0, 0)
      return
    }

    if (window.location.hash) return

    const saved = getSavedScrollPosition('/')
    if (saved !== undefined && saved > 0) {
      window.scrollTo(0, saved)
    } else {
      window.scrollTo(0, 0)
    }
  }, [currentPage])

  /* For Strategy 2: holds eagerly-preloaded components so they render synchronously without Suspense. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [preloadedComponents, setPreloadedComponents] = useState<Record<string, ComponentType<any>>>({})

  /* Sync the module-scope priorityMap with debug config so priority-based lazy loading uses live values. */
  useEffect(() => {
    if (debug.PRIORITY) {
      updatePriorityMap(debug.PRIORITY)
    }
  }, [debug.PRIORITY])

  /* Strategy 2: eagerly import all lazy sections in the background after mount. Hash-targeted section loads
     immediately; remaining sections load in priority order staggered by 200ms each.
     KNOWN ISSUE: The staggered setTimeout calls create multiple timers that are NOT collected
     for cleanup. The effect's return only clears the initial 500ms timeout. If the component
     unmounts before all staggered preloads fire, they will still execute and attempt to update ref/state
     on an unmounted component. This is a known memory leak / state-update-on-unmounted warning. */
  useEffect(() => {
    if (debug.LAZY_LOADING_STRATEGY !== 2 || isMobileDevice()) return

    const lazySectionIds = ['projects', 'side-projects', 'archive', 'education', 'recommendations', 'resume', 'faq']
    const currentHash = window.location.hash.replace('#', '')
    const isHashTarget = currentHash && lazySectionIds.includes(currentHash)

    const importMapBase = importModules

    /* Hash-targeted sections preload immediately, bypassing the 500ms wait.
       This ensures instant rendering when navigating to a section on page load. */
    if (isHashTarget) {
      importMapBase[currentHash]().then((mod) => {
        setPreloadedComponents((prev) => ({ ...prev, [currentHash]: mod.default }))
      })
    }

    /* 500ms initial delay waits for the main content (Hero, About, Experience) to finish rendering.
       200ms stagger between preloads prevents network congestion from simultaneous
       dynamic imports that could degrade TTI (Time to Interactive). */
    const timer = setTimeout(() => {
      const entries = Object.entries(importMapBase)
        .filter(([key]) => !(isHashTarget && key === currentHash))
        .sort(([aId], [bId]) => {
          const pa = debug.PRIORITY?.[aId.toUpperCase() as keyof typeof debug.PRIORITY] ?? 99
          const pb = debug.PRIORITY?.[bId.toUpperCase() as keyof typeof debug.PRIORITY] ?? 99
          return pa - pb
        })

      let delay = 0
      entries.forEach(([key, importFn]) => {
        /* Stagger each preload by 200ms to avoid simultaneous network requests. */
        setTimeout(() => {
          importFn().then((mod) => {
            setPreloadedComponents((prev) => ({ ...prev, [key]: mod.default }))
          })
        }, delay)
        delay += 200
      })
    }, 500)

    return () => clearTimeout(timer)
  }, [debug.LAZY_LOADING_STRATEGY, debug.PRIORITY]) // eslint-disable-line react-hooks/exhaustive-deps

  if (currentPage === '/privacy') {
    return (
      <div className="app" id="top">
        <Suspense fallback={null}>
          <ThemeTransition />
        </Suspense>
        <DebugPanel />
        <Navbar />
        <main className="main-content">
          <PrivacyPage />
        </main>
        <Footer />
      </div>
    );
  }

  if (currentPage === '/terms') {
    return (
      <div className="app" id="top">
        <Suspense fallback={null}>
          <ThemeTransition />
        </Suspense>
        <DebugPanel />
        <Navbar />
        <main className="main-content">
          <TermsPage />
        </main>
        <Footer />
      </div>
    );
  }

  /* Catch-all for unknown paths: renders NotFoundPage with full Navbar/Footer.
     public/404.html remains as a static fallback for when the SPA can't load. */
  if (currentPage !== '/') {
    return (
      <div className="app" id="top">
        <Suspense fallback={null}>
          <ThemeTransition />
        </Suspense>
        <DebugPanel />
        <Navbar />
        <main className="main-content">
          <NotFoundPage />
        </main>
        <Footer />
      </div>
    );
  }

  /* Compute the ordered list of sections to render, based on the current debug configuration and ordering. */
  const activeSections = getActiveSections(debug.SECTIONS, debug.ORDER?.SECTIONS);

  /* Sections that are eagerly imported (always loaded upfront). These render directly without any lazy wrapper. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const builtInComponents: Record<string, ComponentType<any>> = {
    hero: Hero,
    about: MemoizedAbout,
    experience: MemoizedExperience,
    achievements: MemoizedAchievements,
  };

  const importMap = importModules;

  const isMobile = isMobileDevice();

  /* Strategy 1 uses IntersectionObserver (LazyOnViewport) to trigger loading when the section scrolls into view.
     We also use this strategy as a fallback on mobile to avoid preloading/evaluating heavy JS chunks upfront. */
  const useIo = debug.LAZY_LOADING_STRATEGY === 1 || (debug.LAZY_LOADING_STRATEGY === 2 && isMobile);

  /* Render the full page: optional features (custom cursor, canvas bg), Navbar, main section list, and Footer.
     Sections render via one of four paths:
       - Built-in components (line 386): rendered directly, not lazy-loaded
       - Strategy 2 preloaded (lines 394-419): if already preloaded, render instantly; otherwise fall through to Suspense
       - Strategy 1 IntersectionObserver (lines 423-438): IO-driven loading with shimmer fallback
       - Strategy 0 / fallback (lines 445-457): default Suspense with lazyWithPriority
     Three strategies documented in LAZY_LOADING_STRATEGIES.md. Strategy 2 (Background Preload) is the production default.
     Each section is individually wrapped in ErrorBoundary to isolate failures. */
  return (
    <div className="app" id="top">
      <Suspense fallback={null}>
        <ThemeTransition />
      </Suspense>
      <DebugPanel />
      {debug.CUSTOM_CURSOR && !isMobileDevice() && (
        debug.CURSOR_EAGER_LOAD ? (
          <CustomCursor />
        ) : (
          <DelayedMount delay={300}>
            <CustomCursor />
          </DelayedMount>
        )
      )}
      {debug.CANVAS_BACKGROUND && !isMobileDevice() && (
        <Suspense fallback={null}>
          <CanvasBackground />
        </Suspense>
      )}
      <Navbar />
      {typeof window !== "undefined" && /SamsungBrowser/i.test(navigator.userAgent) && (
        <Suspense fallback={null}>
          <SamsungNotice />
        </Suspense>
      )}
      <main className="main-content">
        {activeSections.map((section) => {
          /* Built-in components rendered directly (not lazy-loaded): Hero, About, Experience, Achievements. */
          const BuiltIn = builtInComponents[section.id];
          if (BuiltIn) {
            return <BuiltIn key={section.id} />;
          }

          /* Strategy 2 path: if component was preloaded, render synchronously
             without Suspense. If not yet preloaded, fall through to default Suspense below. */
          const usePreload = debug.LAZY_LOADING_STRATEGY === 2 && !isMobile;

          if (usePreload) {
            const Preloaded = preloadedComponents[section.id]
            if (Preloaded) {
              return (
                <ErrorBoundary key={section.id} sectionName={section.placeholderTitle || section.label}>
                  <Preloaded />
                </ErrorBoundary>
              )
            }
            // Fall through to suspense-based loading if not preloaded yet
            const LazyComponent = lazyComponentMap[section.id]
            if (!LazyComponent) return null
            return (
              <ErrorBoundary key={section.id} sectionName={section.placeholderTitle || section.label}>
                <Suspense
                  fallback={
                    <SectionPlaceholder
                      minHeight={section.placeholderMinHeight || '45vh'}
                      title={section.placeholderTitle || section.label}
                    />
                  }
                >
                  <LazyComponent />
                </Suspense>
              </ErrorBoundary>
            )
          }

          /* Strategy 1 (IntersectionObserver): LazyOnViewport triggers dynamic import when the section's
             placeholder enters the viewport. Loading starts on scroll proximity, not on page load. */
          if (useIo) {
            const importFn = importMap[section.id];
            if (!importFn) return null;
            return (
              <ErrorBoundary key={section.id} sectionName={section.placeholderTitle || section.label}>
                <LazyOnViewport
                  importFn={importFn}
                  fallback={
                    <SectionPlaceholder
                      minHeight={section.placeholderMinHeight || '45vh'}
                      title={section.placeholderTitle || section.label}
                    />
                  }
                />
              </ErrorBoundary>
            );
          }

          /* Strategy 0 / fallback: default Suspense with lazyWithPriority. Chunk loads after
             priority-based delay (see lazyWithPriority above). Suspense shows shimmer until chunk resolves. */
          const LazyComponent = lazyComponentMap[section.id];
          if (!LazyComponent) return null;

          return (
            <ErrorBoundary key={section.id} sectionName={section.placeholderTitle || section.label}>
              <Suspense
                fallback={
                  <SectionPlaceholder
                    minHeight={section.placeholderMinHeight || '45vh'}
                    title={section.placeholderTitle || section.label}
                  />
                }
              >
                <LazyComponent />
              </Suspense>
            </ErrorBoundary>
          );
        })}
      </main>
      {/* Conditionally render the footer based on active section config. */}
      {getFooterEnabled(debug.SECTIONS) && <Footer />}
    </div>
  );
}

export default App;
